import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import type { Locality, Region, State } from '@shared/schema';

import { useGeographyContext } from '@/hooks/useGeographyContext';
import { fetchJson } from '@/lib/queryClient';
import { readPersistedGeography } from '@/lib/geography';
import { trackPublicFunnelEvent } from '@/lib/public-analytics';
import { PublicShell } from '@/components/public-shell';
import { RegionDiscoveryCard } from '@/components/region-discovery-card';
import { OnlineRegionCard } from '@/components/online-region-card';

interface HomeLocality extends Pick<Locality, 'id' | 'name' | 'slug' | 'timezone'> {
  regionId: string | null;
}

interface HomeRegion extends Region {
  localities: HomeLocality[];
}

interface HomeGeography {
  state: State | null;
  physicalRegions: HomeRegion[];
  onlineRegion: Region | null;
}

export default function GeographySelector() {
  const [, navigate] = useLocation();
  const { setGeography } = useGeographyContext();

  // Auto-redirect returning visitors to their saved geography
  useEffect(() => {
    try {
      const { geography, hasPersistedSelection } = readPersistedGeography();
      if (hasPersistedSelection) {
        const { regionSlug, localitySlug } = geography;
        navigate(
          localitySlug ? `/regions/${regionSlug}/${localitySlug}` : `/regions/${regionSlug}`,
          { replace: true }
        );
      }
    } catch {
      // Storage unavailable — stay on discovery screen
    }
  }, [navigate]);

  const { data, isLoading, error } = useQuery<HomeGeography>({
    queryKey: ['geography-home'],
    queryFn: () => fetchJson<HomeGeography>('/api/geography/home'),
    staleTime: 1000 * 60 * 10,
  });

  const { data: publicStats } = useQuery<{
    activeCreators: number;
    availableClasses: number;
    totalStudents: number;
  }>({
    queryKey: ['/api/public-stats'],
  });

  function handleSelectRegion(regionSlug: string) {
    void trackPublicFunnelEvent({
      eventName: 'landing_to_discovery',
      source: 'geography_selector',
      destinationType: regionSlug === 'online' ? 'online_region' : 'region',
      regionSlug,
    });
    setGeography(regionSlug);
    navigate(`/regions/${regionSlug}`);
  }

  function handleSelectLocality(regionSlug: string, localitySlug: string) {
    void trackPublicFunnelEvent({
      eventName: 'landing_to_discovery',
      source: 'geography_selector',
      destinationType: 'locality',
      regionSlug,
      localitySlug,
    });
    setGeography(regionSlug, localitySlug);
    // navigation handled by the Link component in RegionDiscoveryCard
  }

  const { state, physicalRegions = [], onlineRegion } = data ?? {};

  return (
    <PublicShell routeKey="root">
      {/* ── Hero ── */}
      <section className="hero-gradient overflow-hidden border-b border-border/60">
        <div className="page-shell py-14">
          <h1 className="section-title max-w-3xl">Kalovate: Elevate Beauty.</h1>
          <p className="mt-5 max-w-3xl font-serif text-[clamp(1.5rem,2.8vw,2.75rem)] leading-none text-foreground">
            Find beauty in your local community.
          </p>
          <p className="section-copy mt-3 max-w-2xl">
            Choose your region to find local classes, creators and events nearby, or pick a
            neighborhood to see events specific to where you live.
          </p>
          {publicStats && (
            <div className="mt-8 hidden flex-wrap gap-3 sm:flex">
              <StatBadge value={publicStats.activeCreators} label="Creators" />
              <StatBadge value={publicStats.availableClasses} label="Classes" />
              <StatBadge value={publicStats.totalStudents} label="Students" />
            </div>
          )}
        </div>
      </section>

      {/* ── Geography discovery ── */}
      <section className="page-section">
        <div className="page-shell">
          {/* State heading */}
          {state && (
            <div className="mb-8">
              <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/60 px-4 py-1.5">
                <span className="font-condensed text-xs font-bold uppercase tracking-widest text-primary">
                  {state.abbreviation}
                </span>
                <span className="editorial-kicker">{state.name}</span>
              </span>
              <h2 className="mt-4 text-3xl font-semibold text-foreground">
                Choose your region or neighborhood
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Select a region to see everything in that area, or tap a neighborhood pill to go
                directly there.
              </p>
            </div>
          )}

          {/* Loading skeletons */}
          {isLoading && (
            <div className="space-y-4">
              {[1, 2].map(i => (
                <div
                  key={i}
                  className="h-44 animate-pulse rounded-2xl border border-border/70 bg-card"
                />
              ))}
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div className="rounded-2xl border border-border/70 bg-card p-8">
              <p className="text-sm font-medium text-destructive">
                Could not load regions. Check your connection and{' '}
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="underline underline-offset-2"
                >
                  try again
                </button>
                .
              </p>
            </div>
          )}

          {/* Physical region cards */}
          {!isLoading && !error && (
            <>
              {physicalRegions.length === 0 ? (
                <div className="rounded-2xl border border-border/70 bg-card p-8">
                  <p className="text-sm text-muted-foreground">
                    No active regions are published yet.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {physicalRegions.map(region => (
                    <RegionDiscoveryCard
                      key={region.id}
                      region={region}
                      localities={region.localities}
                      onSelectRegion={() => handleSelectRegion(region.slug)}
                      onSelectLocality={localitySlug =>
                        handleSelectLocality(region.slug, localitySlug)
                      }
                    />
                  ))}
                </div>
              )}

              {/* Online region — visually separated at the bottom */}
              {onlineRegion && (
                <div className="mt-10 border-t border-border/50 pt-8">
                  <p className="editorial-kicker mb-4">Not near a physical location?</p>
                  <OnlineRegionCard
                    region={onlineRegion}
                    onSelect={() => handleSelectRegion(onlineRegion.slug)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </PublicShell>
  );
}

function StatBadge({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded-full border border-border/70 bg-muted/60 px-4 py-2 text-sm">
      <span className="font-semibold text-primary">{value.toLocaleString()}</span>{' '}
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}
